import { DUPE_API_URL } from '@dupe/common/constants'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import useAuthUser from '~/src/hooks/useAuthUser'
import fetcher from '~/src/lib/fetcher'
import { UpdateUserPayload } from '~/src/types'

/**
 * Hook for updating user metadata
 * This mutation allows updating a specific metadata field on the user object
 * Note: The API controller is designed to update one metadata key at a time
 */
export function useUpdateUserMetadata() {
  const { authUser } = useAuthUser()
  const queryClient = useQueryClient()

  return useMutation<
    { user: UpdateUserPayload }, // Response type
    Error, // Error type
    { key: string; value: any } // Input type - single key/value for metadata
  >({
    mutationFn: async ({ key, value }) => {
      // The controller expects metadata with a single key to update
      const metadata = { [key]: value }

      return fetcher(`${DUPE_API_URL}/api/user`, {
        method: 'PUT',
        dataOrParams: {
          metadata,
        },
        authUser,
      })
    },
    onSuccess: () => {
      // Invalidate relevant queries to refetch the user data
      queryClient.invalidateQueries({ queryKey: ['user-data'] })
    },
  })
}

export default useUpdateUserMetadata
